import React, { useEffect, useState } from "react";

import { useLocation, useParams } from "react-router-dom";

import { useForm } from "react-hook-form/dist/react-hook-form.ie11";
import { useHistory } from "react-router-dom";

import { fireOnce, objectKeys } from "@ds-developer/frontend";

import { Brand } from "@hubcapp/graphql";
import { getBrandRoute } from "@hubcapp/utils";
import { useAuth, useTealium } from "@hubcapp/context";

import { SignUpFormFields, StateProviderProps } from "./types";
import SignUp from "./SignUp";

import validationSchema from "./validationSchema";

import {
  LAMBDA_VALIDATION_EXCEPTION,
  LIMIT_EXCEEDED_EXCEPTION,
} from "../../../utils/auth";

const SignUpStateProvider: React.FC<StateProviderProps> = ({
  initialFormFields,
}) => {
  const { push } = useHistory();
  const { pathname } = useLocation();
  const { brand } = useParams<{ brand: Brand }>();
  const { trackEvent } = useTealium();
  const { signUp } = useAuth();
  const correctPath = pathname.endsWith("sign-up")
    ? "Sign Up"
    : "Forgotten Password";
  const [primaryErrorMessage, setPrimaryErrorMessage] = useState<string>("");

  const [loading, setLoading] = useState(false);

  const {
    errors,
    formState: { isValid },
    handleSubmit,
    register,
    triggerValidation,
  } = useForm({
    defaultValues: initialFormFields,
    mode: "onChange",
    validationSchema,
  });

  const setInitialValidation = () => {
    if (initialFormFields) {
      triggerValidation(objectKeys(initialFormFields));
    }
  };

  useEffect(setInitialValidation, []);

  const submit = ({ adiNumber, mobileNumber }: SignUpFormFields) => {
    /**
     * Signing up will send you a 2FA token to the registered mobile number
     * Mobile number is used as validation to ensure it matches Cognito Records
     */
    setLoading(true);
    signUp(adiNumber, mobileNumber)
      .then(data => {
        if (data === LIMIT_EXCEEDED_EXCEPTION) {
          setPrimaryErrorMessage("Please try again later or contact support.");
          setLoading(false);
          return;
        }
        if (data === LAMBDA_VALIDATION_EXCEPTION) {
          setPrimaryErrorMessage(
            "Your ADI number and mobile number did not match our records",
          );
          setLoading(false);
          return;
        }
        push(`/${getBrandRoute(brand)}/set-password`);
      })
      .catch(err => {
        setLoading(false);
        throw Error(err);
      });
  };

  const handleADIEvent = () => {
    fireOnce(`${correctPath}|Interaction|ADI Number`, () => {
      trackEvent({
        eventCategory: correctPath,
        eventAction: "Interaction",
        eventLabel: "ADI Number",
      });
    });
  };

  const handleMobileNoEvent = () => {
    fireOnce(`${correctPath}|Interaction|Mobile Number`, () => {
      trackEvent({
        eventCategory: correctPath,
        eventAction: "Interaction",
        eventLabel: "Mobile Number",
      });
    });
  };

  const handleAccessCodeEvent = () => {
    trackEvent({
      eventCategory: correctPath,
      eventAction: "Button Click",
      eventLabel: "Get Access Code",
    });
  };

  const handleSignInEvent = () => {
    trackEvent({
      eventCategory: correctPath,
      eventAction: "Link Click",
      eventLabel: "Sign In",
    });
  };

  const signInPath = `/${getBrandRoute(brand)}/sign-in`;

  return (
    <SignUp
      errors={errors}
      handleSubmit={handleSubmit(submit)}
      isValid={isValid}
      handleADIEvent={handleADIEvent}
      handleMobileNoEvent={handleMobileNoEvent}
      handleAccessCodeEvent={handleAccessCodeEvent}
      handleSignInEvent={handleSignInEvent}
      loading={loading}
      register={register}
      primaryErrorMessage={primaryErrorMessage}
      signInPath={signInPath}
      brand={brand}
    />
  );
};

export default SignUpStateProvider;
